import { Grid, Typography, useTheme } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import {
  DataControlCard,
  DataControlCardProps,
  FormCardViewAttribute,
  RequiredField,
} from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'

import { CargoType } from '@planning/app/api'
import { RailVisitCargoFormStore } from '@planning/pages/RailVisit/stores/RailVisitCargoFormStore'
import { RailVisitCargoSubForm } from './RailVisitCargoSubForm'

export type RailVisitCargoViewProps = {
  store: RailVisitCargoFormStore
}

const RailVisitCargoViewMode = observer(({ store }: RailVisitCargoViewProps) => {
  const { t } = useTranslate()
  const theme = useTheme()

  const { cargoType, dischargeEstimate, loadEstimate } = store.data

  const estimateLabel = `${t('discharge', 'Discharge')}/${t('load', 'Load')}`
  const estimateValue = `${dischargeEstimate}/${loadEstimate}`

  const hasAnyEstimate = !!(dischargeEstimate || loadEstimate)
  const hasCargoType = !!cargoType

  const shouldShow = hasCargoType || hasAnyEstimate

  if (!shouldShow) {
    return (
      <Typography variant='body2' color={theme.palette.text.secondary}>
        {t('noInformationYet', 'No information yet')}
      </Typography>
    )
  }

  return (
    <>
      {hasCargoType && <FormCardViewAttribute label={t('cargo', 'Cargo')} value={cargoType} />}
      {hasAnyEstimate && <FormCardViewAttribute label={estimateLabel} value={estimateValue} />}
    </>
  )
})

type RailVisitCargoEditModeProps = {
  store: RailVisitCargoFormStore
  isCargoTypeLocked: boolean
}

const RailVisitCargoEditMode = observer(
  ({ store, isCargoTypeLocked }: RailVisitCargoEditModeProps) => {
    return (
      <Grid container spacing={2} width={'100%'}>
        <Grid size={{ xs: 12 }}>
          <RailVisitCargoSubForm store={store} isCargoTypeLocked={isCargoTypeLocked} />
        </Grid>
      </Grid>
    )
  },
)

type RailVisitCargoControlCardProps = Partial<DataControlCardProps> & {
  railVisitCargoFormStore: RailVisitCargoFormStore
  isEditMode: boolean
}

export const RailVisitCargoControlCard = observer((props: RailVisitCargoControlCardProps) => {
  const { t } = useTranslate()
  const { railVisitCargoFormStore, isEditMode } = props

  return (
    <DataControlCard
      {...props}
      title={
        <>
          {t('cargo', 'Cargo')} <RequiredField />
        </>
      }
      autoFocusId='dischargeEstimate'
      dataCy='cargo'
      onClickEdit={() => {
        if (!railVisitCargoFormStore.cargoType.value) {
          railVisitCargoFormStore.cargoType.setValue(CargoType.Container)
        }
      }}
      viewComponent={<RailVisitCargoViewMode store={railVisitCargoFormStore} />}
      editComponent={
        <RailVisitCargoEditMode store={railVisitCargoFormStore} isCargoTypeLocked={isEditMode} />
      }
      // isEditMode here referes to the edit mode of the page, not the card
      defaultEditMode={!isEditMode}
    />
  )
})
